import { useEffect, useRef } from "react";

// What a modal owes a keyboard or screen reader user, in one place.
//
// The enquiry and job-application overlays were plain divs: nothing announced
// that a dialog had opened, focus stayed on the page behind while the overlay
// covered it, Tab walked through background content, and Escape did nothing.
// Both now call this, so the behaviour cannot drift apart between them.
//
// Returns a ref to put on the dialog panel. The caller still supplies
// role="dialog", aria-modal and aria-labelledby, because those belong in the
// markup where they can be read.
//
// WCAG 2.1.2 (no keyboard trap), 2.4.3 (focus order), 4.1.2 (name, role, value).
const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export default function useDialog(isOpen, onClose) {
  const dialogRef = useRef(null);

  // Held in a ref so a caller passing an inline arrow does not retrigger the
  // effect on every render, which would steal focus back mid-typing.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    const panel = dialogRef.current;
    if (!panel) return undefined;

    // Where focus came from, so it can be given back. Without this the user
    // is dropped at the top of the document when the dialog closes.
    const returnTo = document.activeElement;

    const focusable = () =>
      [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);

    // Focus the first control rather than the panel, so a screen reader reads
    // the dialog's name and then lands somewhere useful.
    const first = focusable()[0];
    if (first) first.focus();
    else panel.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== "Tab") return;

      // Cycle within the dialog. Recomputed on each Tab because validation
      // errors add and remove elements while the dialog is open.
      const items = focusable();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const edge = event.shiftKey ? items[0] : items[items.length - 1];
      if (document.activeElement === edge) {
        event.preventDefault();
        (event.shiftKey ? items[items.length - 1] : items[0]).focus();
      }
    };

    // Capture phase: the dialog should see Escape before anything underneath.
    document.addEventListener("keydown", onKeyDown, true);

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      if (returnTo instanceof HTMLElement && document.contains(returnTo)) {
        returnTo.focus();
      }
    };
  }, [isOpen]);

  return dialogRef;
}
